<template>
    <div>
    <b-modal id="certificate-modal-action" @change="$emit('updateModalState', false)"
        :dialog-class="'animate-modal-dialog ' + (isExpanded ? 'expanded' : 'shrink')" :visible="visible" hide-header
        centered content-class="certificate-modal-action" size="xl" body-class="modal-body" hide-footer
        modal-class="custom-modal" no-close-on-backdrop>
        <div class="body-container">
            <div class="right-close-panel">
                <button @click="$emit('updateModalState', false)" class="close-button" role="button">
                    <img src="@/assets/images/cancel-icon.svg" />
                </button>
            </div>
            <div class="modal-content-wrapper">
                <div class="step-sidebar">
                    <div class="step-item" @click="jumpToStep(1)"
                        :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
                        <div class="step-circle">1</div>
                        <div class="step-content">
                            <h3 class="step-title">Basic Information</h3>
                            <p class="step-description">Fill certificate information</p>
                        </div>
                    </div>
                    <div class="step-item" @click="jumpToStep(2)"
                        :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
                        <div class="step-circle">2</div>
                        <div class="step-content">
                            <h3 class="step-title">Template Upload</h3>
                            <p class="step-description">Upload HTML template</p>
                        </div>
                    </div>
                    <div class="step-item" @click="jumpToStep(3)"
                        :class="{ active: currentStep >= 3, completed: currentStep > 3 }">
                        <div class="step-circle">3</div>
                        <div class="step-content">
                            <h3 class="step-title">Preview & Create</h3>
                            <p class="step-description">Review and create certificate</p>
                        </div>
                    </div>
                </div>
                <div class="main-content">
                    <div class="info-container expand">
                        <h1 class="modal-title">
                            {{ isEditMode ? 'Edit Certificate' : 'Create Certificate' }}
                        </h1>
                        <div v-show="currentStep === 1" class="step-content">
                            <div v-if="isLoadingFormData" class="step-1-skeleton">
                                <div class="form-group my-3">
                                    <div class="skeleton-label"></div>
                                    <div class="skeleton-input"></div>
                                </div>
                                <div class="form-group my-3">
                                    <div class="skeleton-label"></div>
                                    <div class="skeleton-textarea"></div>
                                </div>
                                <div class="form-group">
                                    <div style="display: flex; align-items:center;">
                                        <div class="skeleton-switch"></div>
                                        <div class="skeleton-validity-inputs">
                                            <div class="skeleton-input-small"></div>
                                            <div class="skeleton-select-small"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else>
                                <div class="form-group my-3">
                                    <label for="title" class="main-label">Certificate Title (Required)</label>
                                    <div class="textfield-wrapper">
                                        <input placeholder="Type your certificate title" v-model="title" class="form-input"
                                            type="search" name="title" id="title" maxlength="255" required>
                                        <div class="text-right text-sm text-gray-500 mt-1">
                                            {{ title ? title.length : 0 }}/255
                                        </div>
                                    </div>
                                </div>
                                <div class="form-group my-3">
                                    <label for="description" class="main-label">Description</label>
                                    <div class="textarea-wrapper">
                                        <textarea
                                            placeholder="Enter certificate description"
                                            v-model="description"
                                            class="form-textarea"
                                            name="description"
                                            id="description"
                                            rows="5"
                                            maxlength="1000">
                                        </textarea>
                                        <div class="text-right text-sm text-gray-500 mt-1">
                                            {{ description ? description.length : 0 }}/1000
                                        </div>
                                    </div>
                                </div>
                                <div class="form-group">
                                    <div style="display: flex; align-items:center;">
                                        <div class="form-check form-switch me-3" style=" margin-top: 19px; width: 200px;">
                                            <input class="form-check-input" type="checkbox" role="switch" id="hasValidity"
                                                v-model="hasValidity" />
                                            <label class="form-check-label" for="hasValidity">Has Validity Period</label>
                                        </div>
                                        <div v-if="hasValidity">
                                            <div class="d-flex gap-3 align-items-center">
                                                <input type="number" class="form-input input-small" v-model="validityValue" min="1" />
                                                <select class="form-select select-small" v-model="validityUnit">
                                                    <option value="days">Days</option>
                                                    <option value="months">Months</option>
                                                    <option value="years">Years</option>
                                                </select>
                                            </div>
                                            <div v-if="hasValidity && (!validityValue || validityValue <= 0)" class="text-danger mt-1">
                                            Validity value cannot be 0 or empty
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-show="currentStep === 2" class="step-content upload-container-wrapper">
                            <div class="upload-container">
                                <label class="main-label mt-3">
                                    Upload HTML Certificate Template 
                                    <span class="text-danger">*</span>
                                </label>
                                <div class="upload-section">
                                    <vue-dropzone 
                                        ref="templateDropzone" 
                                        id="manage-certificate-template-dropzone" 
                                        :options="dropzoneOptions"
                                        @vdropzone-success="onTemplateUploadSuccess"
                                        @vdropzone-error="onTemplateUploadError" 
                                        @vdropzone-removed-file="onTemplateRemoved"
                                        class="template-dropzone">
                                    </vue-dropzone>
                                </div>
                                <p class="allowed-formats">
                                    Allowed formats: <strong class="text-danger">{{ validExtensions.join(", ").toUpperCase() }}</strong>
                                </p>
                            </div>
                            <div class="template-info">
                                <div v-if="exampleTemplateURL" class="example-template-download mt-3">
                                    <a @click.prevent="downloadTemplate" role="button"
                                        class="btn btn-sm btn-primary" >
                                        <i class="fas fa-download mr-1"></i> Download Example Template
                                    </a>
                                </div>
                                <div class="template-help mt-4">
                                    <strong>System Generated Template Placeholders:</strong>
                                    <ul v-pre class="placeholder-list mt-2">
                                        <li><code>{{CertificateTitle}}</code> — Title displayed on the certificate</li>
                                        <li><code>{{UserFullName}}</code> — Full name of the student/user</li>
                                        <li><code>{{IssueDate}}</code> — Date the certificate is issued</li>
                                    </ul>

                                    <small class="d-block mt-3 fw-semibold">(Optional):</small>
                                    <ul v-pre class="placeholder-list mt-2">
                                        <li><code>{{Description}}</code> — Brief description or reason for award</li>
                                        <li><code>{{ValidUntil}}</code> — Date the certificate is valid until</li>
                                    </ul>
                                    <small class="text-muted d-block mt-2">
                                        System generated placeholders will be automatically replaced when generating certificates if you put them in your certificate template.
                                    </small>

                                    <div class="mt-3" v-if="templateContent">
                                        <strong>Available Placeholders in Uploaded Template</strong>
                                        <ul class="placeholder-list">
                                            <li v-if="availablePlaceholders.length === 0" class="text-muted d-block mt-2">No placeholders detected</li>
                                            <span v-for="(ph, index) in availablePlaceholders" :key="index">
                                                <code>{{ ph }}</code>{{  (index + 1) != availablePlaceholders.length ? ',' : '' }}
                                            </span>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-show="currentStep === 3" class="step-content">
                            <div v-if="isLoadingPreview" class="step-3-skeleton">
                                <div class="preview-skeleton-container">
                                    <div class="skeleton-preview-header"></div>
                                    <div class="skeleton-preview-content">
                                        <div class="skeleton-preview-title"></div>
                                        <div class="skeleton-preview-body"></div>
                                        <div class="skeleton-preview-footer"></div>
                                    </div>
                                </div>
                            </div>
                            <div v-else-if="currentStep === 3">
                                <CertificatePreview 
                                    :templateContent="templateContent"
                                    :certificateTitle="title" 
                                    :description="description"
                                    :fullName="'John Doe'" 
                                    :validityPeriodInSeconds="getValidityPeriodInSeconds()"
                                    @onOverflowChange="handleOverflowChange" />
                            </div>
                        </div>
                    </div>

                    <div class="step-btn-container">
                        <div class="step-navigation">
                            <button class="step-btn secondary" @click="$bvModal.show('instructions-modal')">
                                Instructions
                            </button>
                            <button v-if="currentStep > 1" @click="previousStep" class="step-btn secondary">
                                Previous
                            </button>
                            <button v-if="currentStep < totalSteps" :disabled="!isCurrentStepValid" @click="nextStep"
                                class="step-btn">
                                Next
                                <img src="@/assets/images/arrow-right.svg" />
                            </button>
                            <button v-if="currentStep === totalSteps" :disabled="!isFormValid" @click="handleSubmit"
                                class="step-btn">
                                {{ isEditMode ? 'Update' : 'Create' }}
                                <img src="@/assets/images/arrow-right.svg" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </b-modal>

    <b-modal id="instructions-modal" title="Certificate Instructions" hide-footer size="lg" centered>
    <iframe :src="instructionsURL" style="width:100%; height:400px; border:none;"></iframe>
    </b-modal>
    </div>

</template>

<script>
import { mapActions, mapState } from "vuex";
import { POST_FILE_API } from "../../../constants/urls";
import CookieHelpers from "../../../helpers/CookieHelpers";
import CertificatePreview from "./CertificatePreview.vue";
import vue2Dropzone from "vue2-dropzone";
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import dayjs from "dayjs";

export default {
    name: "ManageCertificateModal",
    props: {
        visible: Boolean,
        isEditMode: Boolean,
        certificateData: Object,
    },
    data() {
        return {
            currentStep: 1,
            totalSteps: 3,
            isLoadingFormData: false,
            isLoadingPreview: false,
            isExpanded: false,
            certificate: null,
            title: null,
            description: null,
            templateURL: null,
            templateContent: null,
            fileSize: 0,
            exampleTemplateURL: 'https://assets.learn360lms.com/common-assets/certificate-template.html',
            instructionsURL: 'https://assets.learn360lms.com/common-assets/certificate-instructions.html',
            isTemplateOverflowing: false,
            hasValidity: false,
            validityValue: 1,
            validityUnit: "days",
            availablePlaceholders: [],
            validExtensions: ['.html', '.htm'],
            dropzoneOptions: {
                url: this.getUploadURL,
                method: "POST",
                thumbnailWidth: 150,
                maxFilesize: 20, // in MB
                acceptedFiles: '.html,.htm',
                maxFiles: 1,
                addRemoveLinks: true,
                dictDefaultMessage: "Drop HTML template here or click to upload",
                dictInvalidFileType: "Only HTML files are allowed",
                dictFileTooBig: "File is too big (max 20MB)",
                dictMaxFilesExceeded: "Only one file allowed",
                uploadMultiple: false,
                init: function () {
                    this.on("addedfile", function(file) {
                        if (file.size > this.options.maxFilesize * 1024 * 1024) {
                            this.emit("error", file, this.options.dictFileTooBig);
                            this.removeAllFiles(true)
                        }
                    });

                    this.on("sending", function (file, xhr, formData) {
                        formData.append("entity", "Certificates");
                        formData.append("type", "Template");
                    });
                }
            }
        };
    },
    components: {
        CertificatePreview,
        vueDropzone: vue2Dropzone
    },
    computed: {
        ...mapState("appState", ["apiURL", "systemParameters"]),

        isCurrentStepValid() {
            if (this.currentStep === 1) {
                return this.isStep1Valid;
            } else if (this.currentStep === 2) {
                return this.isStep2Valid;
            } else if (this.currentStep === 3) {
                return this.isStep3Valid;
            }
            return false;
        },
        isStep1Valid() {
            const isTitleValid = this.title?.trim();
            const isValidityValid = !this.hasValidity || (this.validityValue && this.validityValue > 0);
            return isTitleValid && isValidityValid;
        },
        isStep2Valid() {
            return !!this.templateURL;
        },
        isStep3Valid() {
            return !this.isTemplateOverflowing;
        },
        isFormValid() {
            return this.isStep1Valid && this.isStep2Valid && this.isStep3Valid;
        },
        getUploadURL() {
            return this.apiURL + POST_FILE_API;
        },
    },
    methods: {
        ...mapActions("certificate", ["addCertificate", "updateCertificate", "fetchCertificateByID"]),

        nextStep() {
            if (this.currentStep < this.totalSteps && this.isCurrentStepValid) {
                this.currentStep++;
            }
        },
        previousStep() {
            if (this.currentStep > 1) {
                this.currentStep--;
            }
        },
        jumpToStep(stepNumber) {
            if (stepNumber === 1) this.currentStep = stepNumber;
            else if (stepNumber === 2 && this.isStep1Valid) this.currentStep = stepNumber;
            else if (stepNumber === 3 && this.isStep2Valid) {
                this.currentStep = stepNumber;
            }
        },

        async populateFormData() {
            if (!this.isEditMode || !this.certificateData) return;
            this.isLoadingFormData = true;
            try {
                this.certificate = await this.fetchCertificateByID(this.certificateData.id) || this.certificateData;
                Object.assign(this, {
                    title: this.certificate.title,
                    description: this.certificate.description,
                    templateURL: this.certificate.templateURL,
                });

                this.setValidityPeriod(this.certificate.validityPeriodInSeconds);
                this.fetchTemplateContent(this.certificate.templateURL);

            }
            catch(error) {
                this.showError(error?.message || 'Failed to load existing certificate.');
            } 
            finally {
                this.isLoadingFormData = false;
            }
        },
        handleSubmit() {
            const certificateModel = {
                id: this.certificateData?.id,
                title: this.title,
                description: this.description,
                templateURL: this.templateURL,
                validityPeriodInSeconds: this.getValidityPeriodInSeconds()
            };

            const certificateAction = this.isEditMode ? this.updateCertificate : this.addCertificate;
            certificateAction(certificateModel).then(this.handleSuccess).catch(this.handleError);
        },
        initializeDropZone() {
            this.dropzoneOptions.url = this.getUploadURL;
            this.dropzoneOptions.headers = {
                'AuthToken': 'System123',
                'Authorization': `Bearer ${CookieHelpers.getCookie("token")}`,
                'Accept': 'application/json',
            }
        },
        extractPlaceholders() {
            if (!this.templateContent) {
                this.availablePlaceholders = [];
                return;
            }

            const regex = /{{([A-Za-z0-9]+)}}/g;
            const matches = [...this.templateContent.matchAll(regex)];
            this.availablePlaceholders = matches.map(m => m[0]);
        },
        onTemplateUploadSuccess(file, response) {
            try {
                const responseData = typeof response === "string" ? JSON.parse(response) : response;
                this.templateURL = responseData.payload;
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.templateContent = e.target.result;
                    this.extractPlaceholders(); 
                };
                reader.readAsText(file);
                this.showSuccess("Template uploaded successfully");
            } catch (error) {
                this.showError("Failed to process upload response");
                this.$refs.templateDropzone.removeFile(file);
            }
        },
        onTemplateUploadError(file, message, xhr) {
            let errorMessage = message;

            if (xhr && xhr.status) {
                switch (xhr.status) {
                    case 401:
                        errorMessage = 'Authentication failed. Please login again.';
                        break;
                    case 403:
                        errorMessage = 'You do not have permission to upload files.';
                        break;
                    case 500:
                        errorMessage = 'Server error. Please try again later.';
                        break;
                    default:
                        errorMessage = `Upload failed with status ${xhr.status}`;
                }
            }

            this.showError(errorMessage);
        },
        onTemplateRemoved() {
            if (this.currentStep === 2) {
                this.templateURL = null;
                this.templateContent = null;
            }
        },
        async addExistingFileToDropzone(templateURL) {
            if (!this.$refs.templateDropzone || !templateURL) return;

            try {
                const dropzone = this.$refs.templateDropzone.dropzone;
                // its necessary to not to trigger onTemplateRemoved()
                dropzone.removeAllFiles(true);

                const mockFile = {
                    name: 'Template.html',
                    size: this.fileSize,
                    type: 'text/html',
                    status: 'success',
                    accepted: true,
                    isExistingFile: true,
                    serverURL: templateURL
                };

                this.$refs.templateDropzone.manuallyAddFile(mockFile, templateURL);

            } catch (error) {
                this.showError('Error adding existing file to dropzone');
            }
        },

        handleOverflowChange({ width, height, hasOverflow }) {
            this.isTemplateOverflowing = hasOverflow;
        },
        closeModal() {
            this.$emit("updateModalState", false);
            this.resetModel();
        },
        resetModel() {
            Object.assign(this.$data, this.$options.data.call(this));
        },
        async fetchTemplateContent(url) {
            try {
                this.isLoadingPreview = true;
                const response = await fetch(url);
                if (!response.ok) throw new Error();
                this.templateContent = await response.text();
                this.fileSize = new Blob([this.templateContent]).size;
                await this.addExistingFileToDropzone(this.certificate.templateURL);
                this.extractPlaceholders()
            } catch (error) {
                this.showError('Failed to fetch template content.');
            } finally {
                this.isLoadingPreview = false;
            }
        },
        setValidityPeriod(seconds) {
            if (!seconds || seconds <= 0) {
                this.hasValidity = false;
                this.validityValue = 1;
                this.validityUnit = "days";
                return;
            }

            this.hasValidity = true;

            const now = dayjs();
            const validUntil = now.add(seconds, "second");

            const diffYears = validUntil.diff(now, "year", true);
            if (diffYears >= 1 && Math.abs(diffYears - Math.round(diffYears)) < 0.01) {
                this.validityValue = Math.round(diffYears);
                this.validityUnit = "years";
                return;
            }

            const diffMonths = validUntil.diff(now, "month", true);
            if (diffMonths >= 1 && Math.abs(diffMonths - Math.round(diffMonths)) < 0.01) {
                this.validityValue = Math.round(diffMonths);
                this.validityUnit = "months";
                return;
            }

            const diffDays = validUntil.diff(now, "day", true);
            this.validityValue = Math.round(diffDays);
            this.validityUnit = "days";
        },
        getValidityPeriodInSeconds() {
            if (!this.hasValidity || !this.validityValue || this.validityValue <= 0) return null;
            const now = dayjs();
            const future = now.add(this.validityValue, this.validityUnit);
            return future.diff(now, "second");
        },
        handleSuccess() {
            this.showSuccess(this.isEditMode ? "Certificate updated successfully." : "Certificate created successfully.");
            this.closeModal();
        },
        handleError(error) {
            this.showError(error.message);
        },
        showError(message, options = {}) {
            this.$toasted.error(message, { icon: "exclamation-triangle", theme: "outline", position: "bottom-center", singleton: true, duration: 3000, ...options });
        },
        showSuccess(message, options = {}) {
            this.$toasted.success(message, { icon: "check", theme: "outline", position: "bottom-center", duration: 3000, ...options });
        },
        resetValidity() {
            if (!this.hasValidity) {
                this.validityValue = null;
                this.validityUnit = "days";
            }
        },
        async initialize() {
            this.currentStep = 1;
            this.initializeDropZone()
            // data layer
            await this.populateFormData();
        },
        async downloadTemplate() {
            try {
                const response = await fetch(this.exampleTemplateURL);
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'sample-certificate-template.html';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                this.showError("Unable to download example template.");
            }
        }
    },
    watch: {
        visible(isCurrentlyVisible) {
            if (isCurrentlyVisible) { this.initialize(); }
        },
        hasValidity: "resetValidity",
    },
};
</script>

<style>
.certificate-modal-action .modal-body.modal-body {
    display: flex;
    flex-direction: row;
    padding: 0 !important;
    overflow: hidden;
}

.certificate-modal-action .modal-body.modal-body .body-container {
    padding: 0;
    display: flex;
    flex-direction: column;
    width: 100%;
    position: relative;
}

#certificate-modal-action .expanded {
    max-width: 96%;
    max-height: 96%;
}

#certificate-modal-action .expanded .certificate-modal-action {
    height: 90vh;
}

#manage-certificate-template-dropzone .dz-preview:not(.dz-error) .dz-details {
  background-color: #21cf47 !important;
}
</style>


<style scoped>
/* =========================
   Skeleton Loading
========================= */
@keyframes skeleton-loading {
    0% {
        background-position: -200px 0;
    }

    100% {
        background-position: calc(200px + 100%) 0;
    }
}

.skeleton-base,
.step-1-skeleton .skeleton-label,
.step-1-skeleton .skeleton-input,
.step-1-skeleton .skeleton-textarea,
.step-1-skeleton .skeleton-switch,
.step-1-skeleton .skeleton-input-small,
.step-1-skeleton .skeleton-select-small,
.step-3-skeleton .skeleton-preview-header,
.step-3-skeleton .skeleton-preview-title,
.step-3-skeleton .skeleton-preview-body,
.step-3-skeleton .skeleton-preview-footer {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200px 100%;
    animation: skeleton-loading 1.5s infinite;
    border-radius: 4px;
}

.step-1-skeleton .skeleton-label {
    height: 16px;
    width: 150px;
    margin-bottom: 8px;
}

.step-1-skeleton .skeleton-input {
    height: 43px;
    width: 100%;
    margin-bottom: 20px;
}

.step-1-skeleton .skeleton-textarea {
    height: 120px;
    width: 100%;
    margin-bottom: 20px;
}

.step-1-skeleton .skeleton-switch {
    height: 20px;
    width: 200px;
    margin-right: 20px;
}

.step-1-skeleton .skeleton-validity-inputs {
    display: flex;
    gap: 10px;
}

.step-1-skeleton .skeleton-select-small,
.step-1-skeleton .skeleton-input-small {
    height: 43px;
    width: 200px;
}

.preview-skeleton-container {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 20px;
    background: white;
}

.step-3-skeleton .skeleton-preview-header {
    height: 40px;
    width: 100%;
    margin-bottom: 20px;
}

.step-3-skeleton .skeleton-preview-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.step-3-skeleton .skeleton-preview-title {
    height: 32px;
    width: 100%;
}

.step-3-skeleton .skeleton-preview-body {
    height: 200px;
    width: 100%;
}

.step-3-skeleton .skeleton-preview-footer {
    height: 24px;
    width: 100%;
}

/* =========================
   Stepper Sidebar
========================= */
.modal-content-wrapper {
    display: flex;
    gap: 0;
    flex: 1;
}

.step-sidebar {
    display: flex;
    width: 300px;
    flex-shrink: 0;
    background: #f5f5f5;
    padding: 2.5rem 0;
    flex-direction: column;
    justify-content: center;
}

.step-item {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem 0;
    border-left: 2px solid #e5e7eb;
    padding-left: 2rem;
    margin: 1rem 0;
}

.step-item.active,
.step-item.completed {
    border-left-color: var(--primary-color);
}

.step-circle {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #e5e7eb;
    color: #6b7280;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 14px;
    flex-shrink: 0;
    position: relative;
    z-index: 1;
}

.step-item.active .step-circle,
.step-item.completed .step-circle {
    background: var(--primary-color);
    color: white;
}

.step-content {
    padding: 0 2rem;
}

.step-title {
    font-size: 16px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 0.25rem 0;
}

.step-item.active .step-title {
    color: var(--primary-color);
}

.step-description {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
}

/* =========================
   Main Layout
========================= */
.main-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
}

.right-close-panel {
    position: absolute;
    top: 2.5rem;
    right: 2rem;
    display: flex;
}

.right-close-panel .close-button {
    position: initial;
}

.info-container.expand .step-content {
    height: 60vh;
    overflow: auto;
}

.input-small {
    width: 200px;
    height: 43px;
}

.select-small {
    width: 200px;
    height: 43px;
    border: none;
    border-bottom: 1.5px solid var(--border-color);
    background-color: transparent;
    font-size: 0.9rem;
}

/* =========================
   Upload Section
========================= */
.upload-container-wrapper {
    display: flex;
    align-items: center;
    gap: 2rem;
    padding-bottom: 20px;
}

.upload-container {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.upload-section {
    margin-bottom: 1rem;
}

.allowed-formats {
    font-size: 0.85rem;
    color: #555;
    margin-top: 1rem;
}
.template-info {
    flex: 1;
    padding-top: 30px;
}

.template-dropzone {
    border: 2px dashed #e5e7eb;
    border-radius: 8px;
    padding: 2rem;
    text-align: center;
    transition: border-color 0.3s ease;
    width: 100%;
    max-width: 400px;
}

.template-dropzone:hover {
    border-color: var(--primary-color);
}

.upload-error {
    color: #ef4444;
    font-size: 0.875rem;
    margin-top: 0.5rem;
    padding: 0.5rem;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 4px;
}

.example-template-download .btn {
    background-color: transparent;
    color: var(--primary-color);
    border: 1px solid var(--primary-color);
    padding: .3rem .7rem;
    border-radius: 6px;
    font-weight: 500;
    font-size: 14px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
}
.example-template-download .btn:hover {
    background-color: var(--primary-color);
    color: white;
}

.placeholder-list {
    list-style: none;
    padding-left: 0;
    margin-top: 8px;
}

/* =========================
   Navigation Buttons
========================= */
.step-navigation {
    display: flex;
    align-items: center;
    gap: 1rem;
    width: 100%;
    padding: 2rem;
}

.step-btn.secondary {
    background: white;
    border: 1px solid #e5e7eb;
    color: #374151;
}

.step-btn.secondary:hover {
    background: #f9fafb;
}

.step-btn.secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* =========================
   Form Inputs
========================= */
.info-container .main-label {
    font-weight: 500;
    font-size: 1rem;
    line-height: 1.37;
    text-transform: uppercase;
    color: #727272;
    margin-bottom: 1rem;
}

.form-input {
    border: 0;
    border-bottom: 1.5px solid #b2b2b2;
    font-size: 1rem;
    outline: none;
    padding: 0.875rem 0;
    width: 100%;
}

.form-input:focus {
    border-color: var(--primary-color) !important;
}

.form-textarea {
    width: 100%;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 14px;
    line-height: 1.5;
    resize: vertical;
    transition: border-color 0.2s ease-in-out;
}

.form-textarea:focus {
    border-color: var(--primary-color);
    outline: none;
    box-shadow: 0 0 0 0.1rem rgba(0, 123, 255, 0.25);
}

/* =========================
   Modal Title
========================= */
.modal-title {
    color: #000;
    font-family: "Circular Std";
    font-size: 2rem;
    font-style: normal;
    font-weight: 450;
    line-height: 1.5rem;
    margin: 3.5rem 2rem 2rem;
    text-transform: capitalize;
}

/* =========================
   Responsive
========================= */
@media (max-width: 768px) {
    .step-sidebar .step-item .step-content {
        display: none;
    }
}
</style>
